import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { LayoutService } from "../../../librettoEditor/services/LayoutService";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { EngineInternalEventsEnum } from "../../../engine/EngineInternalEvents";
import { CompositionEventsEnum } from "../../../librettoEditor/CompositionEvents";
import { FocusStore } from "../../../state/FocusStore";
import { isScriptClip, ScriptLayerClip } from "../../../engine/clips";
import type { SpeakerOverride } from "../../../engine/clips/script/script.interfaces";
import {
    BUILTIN_LAYOUT_PACK,
    DEFAULT_LAYOUT_PACK_ID,
    formatLayoutRef,
    normalizeLayoutId,
} from "../../../librettoEditor/layouts/LayoutPacks";
import { LayoutPacksStore } from "../../../state/LayoutPacksStore";

type GetTranscriptFn = (params: { transcriptId: string; projectId: string }) => Promise<any | undefined>;

type MulticamStyleOption = "automatic" | "active_speaker_only";
type MulticamCutawaysOption = "occasional" | "frequent";

type MulticamWord = {
    startSec: number;
    endSec: number;
    sourceIndex: number;
    diarizationSpeakerId?: string;
    speakerKey: string;
    firestoreSpeakerId?: string;
};

type SpeakerTurn = {
    startSec: number;
    endSec: number;
    speakerKey: string;
    firestoreSpeakerId?: string;
};

type MulticamShot =
    | { kind: "camera"; startSec: number; endSec: number; primary: SpeakerTurn }
    | { kind: "two_up"; startSec: number; endSec: number; primary: SpeakerTurn; secondary: SpeakerTurn };

const toFinite = (v: any, fallback: number): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
};

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

const chooseBestOverride = (matching: SpeakerOverride[]): SpeakerOverride => {
    const overrideSpecificity = (o: SpeakerOverride): number => {
        let score = 0;
        if (o.sourceIndex != null) score += 1;
        if ((o as any).diarizationSpeakerId) score += 2;
        return score;
    };
    const overrideDuration = (o: SpeakerOverride): number => {
        const start = Number(o.startSec ?? 0);
        const end = Number(o.endSec ?? 0);
        return Math.max(0, end - start);
    };
    const chooseBetter = (a: SpeakerOverride, b: SpeakerOverride): SpeakerOverride => {
        const durA = overrideDuration(a);
        const durB = overrideDuration(b);
        if (durA !== durB) return durB < durA ? b : a; // narrower wins

        const specA = overrideSpecificity(a);
        const specB = overrideSpecificity(b);
        if (specA !== specB) return specB > specA ? b : a; // more specific wins

        if (a.startSec !== b.startSec) return b.startSec > a.startSec ? b : a; // later wins
        return b.id > a.id ? b : a;
    };
    return matching.reduce(chooseBetter);
};

const getEffectiveSpeakerForWord = (
    word: {
    startSec: number;
    sourceIndex: number;
    diarizationSpeakerId?: string;
    },
    collapseDiarizationBySource: boolean
): { speakerKey: string; firestoreSpeakerId?: string } => {
    const wordTime = Number(word.startSec ?? 0);
    const speakerOverrides = Array.isArray(ScriptLayerStore.speakerOverrides) ? ScriptLayerStore.speakerOverrides : [];

    const matching = speakerOverrides.filter((override) => {
        if (wordTime < override.startSec || wordTime >= override.endSec) return false;
        if (override.sourceIndex != null && override.sourceIndex !== word.sourceIndex) return false;
        const diar = (override as any).diarizationSpeakerId;
        if (diar && word.diarizationSpeakerId && String(diar) !== String(word.diarizationSpeakerId)) return false;
        if (diar && !word.diarizationSpeakerId) return false;
        return true;
    });

    if (matching.length) {
        const o = chooseBestOverride(matching);
        const speakerId = typeof (o as any)?.speakerId === "string" ? String((o as any).speakerId) : undefined;
        const speakerKey = speakerId ? `speaker-${speakerId}` : `override-${o.id}`;
        return { speakerKey, firestoreSpeakerId: speakerId };
    }

    // In "multicam" contexts we prefer to treat each audio source as a single speaker.
    // This avoids noisy diarization splitting within a source when we have multiple sources.
    if (collapseDiarizationBySource) {
        return { speakerKey: `source-${word.sourceIndex}` };
    }

    const diar = word.diarizationSpeakerId ? String(word.diarizationSpeakerId) : "default";
    return { speakerKey: `source-${word.sourceIndex}-${diar}` };
};

const buildEditedWordsForMulticam = async (args: {
    getTranscript: GetTranscriptFn;
    projectId: string;
    scriptClip: ScriptLayerClip;
}): Promise<MulticamWord[]> => {
    const scriptClip = args.scriptClip;
    const audioEntries: any[] = scriptClip.getAudioEntries();
    const collapseDiarizationBySource = Array.isArray(audioEntries) && audioEntries.length >= 2;
    const getTranscript = args.getTranscript;
    const projectId = args.projectId;

    const words: Array<{
        startSec: number;
        endSec: number;
        sourceIndex: number;
        diarizationSpeakerId?: string;
    }> = [];

    for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
        const entry: any = audioEntries[sourceIndex];
        const baseGain = Number(entry?.gain ?? 1);
        if (Number.isFinite(baseGain) && baseGain <= 0.001) continue;

        const assetId = String(entry?.assetId ?? "");
        if (!assetId) continue;
        const trackRecord = ProjectAssetsStore.projectTracks.find((t: any) => t?.id === assetId || t?.trackId === assetId);
        const transcriptId = String(trackRecord?.activeTranscriptId ?? "");
        if (!transcriptId) continue;

        const transcriptJson = await getTranscript({ transcriptId, projectId });
        if (!transcriptJson?.words || !Array.isArray(transcriptJson.words)) continue;

        const sourceDuration = Number(trackRecord?.duration ?? 0) || 0;

        const entrySegments: Array<{ offsetSec: number; playbackRate: number; rangeInSec: number; rangeOutSec: number }> = [];
        const segmentsRaw: any[] = Array.isArray(entry?.segments) && entry.segments.length > 0 ? entry.segments : [];
        if (segmentsRaw.length > 0) {
            for (const seg of segmentsRaw) {
                const segOffset = toFinite(seg?.offsetSec, 0);
                const segRate = toFinite(seg?.playbackRate ?? entry?.playbackRate, 1) || 1;
                const segIn = toFinite(seg?.range?.inSec, 0);
                const segOut = toFinite(seg?.range?.outSec, 0);
                if (!Number.isFinite(segOffset) || !Number.isFinite(segRate) || segRate <= 0) continue;
                if (!Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
                entrySegments.push({ offsetSec: segOffset, playbackRate: segRate, rangeInSec: segIn, rangeOutSec: segOut });
            }
        } else {
            const rangeInSec = toFinite(entry?.range?.inSec, 0);
            const rangeOutSec = toFinite(entry?.range?.outSec, sourceDuration > 0 ? sourceDuration : Infinity);
            const offsetSec = toFinite(entry?.offsetSec, 0);
            const playbackRate = toFinite(entry?.playbackRate, 1) || 1;
            entrySegments.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
        }

        const MIN_ENERGY_RATIO = 0.2;
        for (const seg of entrySegments) {
            for (const w of transcriptJson.words) {
                if ((w?.type ?? "word") !== "word") continue;
                if (w.energy?.ratio != null && w.energy.ratio > 0 && w.energy.ratio < MIN_ENERGY_RATIO) continue;

                const wordStart = toFinite(w?.t0 ?? w?.start, 0);
                const wordEnd = toFinite(w?.t1 ?? w?.end, wordStart);
                if (wordStart < seg.rangeInSec || wordEnd > seg.rangeOutSec) continue;

                const adjustedStart = (wordStart - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
                const adjustedEnd = (wordEnd - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;

                const speakerIdRaw = w?.speakerId ?? w?.speaker_id;
                const diarizationSpeakerId = speakerIdRaw != null ? String(speakerIdRaw) : undefined;

                words.push({
                    startSec: adjustedStart,
                    endSec: adjustedEnd,
                    sourceIndex,
                    diarizationSpeakerId,
                });
            }
        }
    }

    words.sort((a, b) => a.startSec - b.startSec);

    const visible: MulticamWord[] = [];
    for (const w of words) {
        const t = Number(w.startSec ?? 0);
        if (ScriptLayerStore.isTimeInDeleteCut(t)) continue;
        if (ScriptLayerStore.isTimeInGap(t)) continue;
        if (ScriptLayerStore.isTimeInTranscriptHiddenRange(t)) continue;

        const eff = getEffectiveSpeakerForWord(w, collapseDiarizationBySource);
        visible.push({
            startSec: w.startSec,
            endSec: w.endSec,
            sourceIndex: w.sourceIndex,
            diarizationSpeakerId: w.diarizationSpeakerId,
            speakerKey: eff.speakerKey,
            firestoreSpeakerId: eff.firestoreSpeakerId,
        });
    }

    return visible;
};

const buildSpeakerTurns = (words: MulticamWord[], clipStartSec: number, clipEndSec: number): SpeakerTurn[] => {
    if (!words.length) return [];
    const turns: SpeakerTurn[] = [];

    let cur = words[0];
    let curKey = cur.firestoreSpeakerId ?? cur.speakerKey;
    let start = clipStartSec;
    let end = Math.max(clipStartSec, Number(cur.endSec ?? cur.startSec ?? clipStartSec));

    // If first word starts after clipStart, we still anchor the first scene at clipStart with that speaker.
    start = clipStartSec;

    for (let i = 1; i < words.length; i++) {
        const w = words[i];
        const key = w.firestoreSpeakerId ?? w.speakerKey;
        if (key === curKey) {
            end = Math.max(end, Number(w.endSec ?? w.startSec ?? end));
            continue;
        }
        // Close current turn at the next word start (covers silence as part of previous scene).
        const nextStart = Number(w.startSec ?? end);
        turns.push({
            startSec: start,
            endSec: Math.max(end, nextStart),
            speakerKey: curKey,
            firestoreSpeakerId: cur.firestoreSpeakerId,
        });
        cur = w;
        curKey = key;
        start = nextStart;
        end = Math.max(nextStart, Number(w.endSec ?? w.startSec ?? nextStart));
    }

    turns.push({
        startSec: start,
        endSec: Math.max(end, clipEndSec),
        speakerKey: curKey,
        firestoreSpeakerId: cur.firestoreSpeakerId,
    });

    // Clamp and drop empties
    const EPS = 1e-3;
    return turns
        .map((t) => ({
            ...t,
            startSec: clamp(Number(t.startSec ?? clipStartSec), clipStartSec, clipEndSec),
            endSec: clamp(Number(t.endSec ?? clipEndSec), clipStartSec, clipEndSec),
        }))
        .filter((t) => Number.isFinite(t.startSec) && Number.isFinite(t.endSec) && t.endSec > t.startSec + EPS)
        .sort((a, b) => a.startSec - b.startSec);
};

const mergeShortTurns = (turns: SpeakerTurn[], minDurationSec: number): SpeakerTurn[] => {
    if (!turns.length) return [];
    const out: SpeakerTurn[] = [];
    for (const t of turns) {
        const dur = Number(t.endSec ?? 0) - Number(t.startSec ?? 0);
        if (out.length === 0) {
            out.push({ ...t });
            continue;
        }
        if (dur < minDurationSec) {
            // Merge into previous (keep previous speaker)
            out[out.length - 1].endSec = Math.max(out[out.length - 1].endSec, t.endSec);
            continue;
        }
        // Merge if same speaker as previous
        const prev = out[out.length - 1];
        if (prev.speakerKey === t.speakerKey) {
            prev.endSec = Math.max(prev.endSec, t.endSec);
            continue;
        }
        out.push({ ...t });
    }
    return out;
};

const scoreCameraAtTime = (trackId: string | null, tSec: number): number => {
    if (!trackId) return 0;
    const kf = FocusStore.getKeyframeAtTimeSec(trackId, tSec);
    if (!kf) return 0;
    const faces = Number((kf as any).numFaces ?? NaN);
    const face = (kf as any).face ?? null;

    let score = 0;
    if (Number.isFinite(faces) && faces > 1) score -= 1.5;
    if (face && typeof face === "object") {
        const det = Number(face.detectionConfidence ?? NaN);
        const lm = Number(face.landmarkingConfidence ?? NaN);
        if (Number.isFinite(det)) score += det * 1.0;
        if (Number.isFinite(lm)) score += lm * 0.6;

        const pan = Number(face.panAngle ?? NaN);
        const tilt = Number(face.tiltAngle ?? NaN);
        if (Number.isFinite(pan)) score -= Math.min(1.2, Math.abs(pan) / 45) * 0.6;
        if (Number.isFinite(tilt)) score -= Math.min(1.2, Math.abs(tilt) / 45) * 0.4;

        // Likelihood enums from Vision are strings; treat anything containing "VERY_LIKELY" or "LIKELY" as a penalty.
        const penaltyForLikelihood = (v: any): number => {
            const s = String(v ?? "").toUpperCase();
            if (s.includes("VERY_LIKELY")) return 0.9;
            if (s.includes("LIKELY")) return 0.5;
            if (s.includes("POSSIBLE")) return 0.2;
            return 0;
        };
        score -= penaltyForLikelihood(face.blurred) * 1.0;
        score -= penaltyForLikelihood(face.underExposed) * 0.8;
    }
    return score;
};

const pickCameraForSpeaker = (args: {
    scriptClip: ScriptLayerClip;
    speakerId?: string;
    speakerKey: string;
    tSec: number;
}): number => {
    const scriptClip: any = args.scriptClip;
    const setup: any = (scriptClip as any).multicamCameraSetup;

    const videoEntries: any[] = typeof scriptClip.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];
    if (!videoEntries.length) return 0;
    const audioEntries: any[] = typeof scriptClip.getAudioEntries === "function" ? scriptClip.getAudioEntries() : [];

    const cfgs: any[] = typeof scriptClip.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
    const trackIdForVideoIndex = (idx: number): string | null => {
        try {
            const entry = videoEntries[idx];
            const cfg = cfgs.find((c: any) => c?.input === entry?.input);
            const tid = String(cfg?.assetId ?? "");
            return tid || null;
        } catch {}
        return null;
    };

    const speakerId = args.speakerId ? String(args.speakerId) : null;
    if (setup?.version === 1 && Array.isArray(setup.assignments) && speakerId) {
        const cams = setup.assignments
            .filter((a: any) => a?.kind === "speaker" && String(a?.speakerId ?? "") === speakerId)
            .map((a: any) => Number(a?.sourceIndex))
            .filter((n: any) => Number.isFinite(n) && n >= 0 && n < videoEntries.length);
        if (cams.length === 1) return cams[0];
        if (cams.length > 1) {
            let best = cams[0];
            let bestScore = -Infinity;
            for (const idx of cams) {
                const score = scoreCameraAtTime(trackIdForVideoIndex(idx), args.tSec);
                if (score > bestScore) {
                    bestScore = score;
                    best = idx;
                }
            }
            return best;
        }
    }

    // Fallback: if this speakerKey represents an audio source index, map to the matching video entry by input.
    // This allows automatic multicam to work even before speakers are explicitly labeled.
    try {
        const match = String(args.speakerKey ?? "").match(/^source-(\d+)(?:$|-)/);
        const audioIdx = match ? Number(match[1]) : NaN;
        if (Number.isFinite(audioIdx) && audioIdx >= 0 && audioIdx < audioEntries.length) {
            const input = audioEntries[audioIdx]?.input;
            if (input) {
                const vIdx = videoEntries.findIndex((v: any) => v?.input === input);
                if (vIdx >= 0) return vIdx;
            }
        }
    } catch {}

    // Fallback: just pick the first camera (or try to avoid shared shots)
    let fallback = 0;
    let bestScore = -Infinity;
    for (let i = 0; i < videoEntries.length; i++) {
        const score = scoreCameraAtTime(trackIdForVideoIndex(i), args.tSec);
        if (score > bestScore) {
            bestScore = score;
            fallback = i;
        }
    }
    return fallback;
};

const planShotsFromTurns = (args: {
    turns: SpeakerTurn[];
    style: MulticamStyleOption;
    cutaways: MulticamCutawaysOption;
}): MulticamShot[] => {
    const turns = args.turns ?? [];
    if (turns.length === 0) return [];

    if (args.style === "active_speaker_only") {
        return turns.map((t) => ({ kind: "camera", startSec: t.startSec, endSec: t.endSec, primary: t }));
    }

    // "Automatic": keep active speaker shots, with occasional 2-up establish shots at speaker changes.
    const establishSec = args.cutaways === "frequent" ? 1.0 : 0.65;
    const minGapBetweenEstablishSec = args.cutaways === "frequent" ? 4.0 : 9.0;
    const minShotSec = 0.2;

    const firstDifferentSpeakerFrom = (idx: number): SpeakerTurn | null => {
        const base = turns[idx]?.speakerKey;
        if (!base) return null;
        for (let i = idx + 1; i < turns.length; i++) {
            if (turns[i].speakerKey !== base) return turns[i];
        }
        return null;
    };

    let lastEstablishAt = -Infinity;
    const shots: MulticamShot[] = [];

    for (let i = 0; i < turns.length; i++) {
        const t = turns[i];
        const prev = i > 0 ? turns[i - 1] : null;
        const dur = Number(t.endSec) - Number(t.startSec);

        const speakerChanged = prev ? prev.speakerKey !== t.speakerKey : false;
        const shouldEstablish =
            (i === 0 || speakerChanged) &&
            Number.isFinite(dur) &&
            dur >= establishSec + minShotSec &&
            t.startSec - lastEstablishAt >= minGapBetweenEstablishSec;

        const secondary = i === 0 ? firstDifferentSpeakerFrom(0) : prev;
        if (shouldEstablish && secondary && secondary.speakerKey !== t.speakerKey) {
            const estStart = t.startSec;
            const estEnd = Math.min(t.endSec, estStart + establishSec);
            if (estEnd - estStart >= minShotSec) {
                shots.push({ kind: "two_up", startSec: estStart, endSec: estEnd, primary: t, secondary });
                lastEstablishAt = estStart;
            }
            if (estEnd < t.endSec - 1e-3) {
                shots.push({ kind: "camera", startSec: estEnd, endSec: t.endSec, primary: t });
            }
            continue;
        }

        shots.push({ kind: "camera", startSec: t.startSec, endSec: t.endSec, primary: t });
    }

    // Coalesce adjacent camera shots for the same speaker (defensive).
    const merged: MulticamShot[] = [];
    for (const s of shots) {
        const prev = merged[merged.length - 1];
        if (
            prev &&
            prev.kind === "camera" &&
            s.kind === "camera" &&
            prev.primary.speakerKey === s.primary.speakerKey &&
            Math.abs(prev.endSec - s.startSec) < 1e-3
        ) {
            (prev as any).endSec = s.endSec;
            continue;
        }
        merged.push(s);
    }
    return merged;
};

const hideAllLayoutManagedClips = (): void => {
    try {
        const comp: any = CompositionService.composition;
        if (!comp) return;
        for (const tr of comp.tracks ?? []) {
            for (const c of (tr as any)?.clips ?? []) {
                const name = String((c as any)?.name ?? "");
                if (!name.startsWith((LayoutService as any).LAYOUT_MANAGED_PREFIX ?? "__libretto_layout__")) continue;
                try { if (typeof (c as any).alpha === "number") (c as any).alpha = 0; } catch {}
                try { CompositionService.notifyClipUpdated(c); } catch {}
            }
        }
    } catch {}
};

/**
 * Apply a deterministic "baseline" multicam edit.
 * V1 behavior:
 * - Overwrites scene boundaries (scene splits) based on speaker turn-taking.
 * - Applies the Camera layout per scene using the configured Camera setup.
 * - Cutaways frequency controls minimum scene duration (merge short turns).
 *
 * This is intentionally conservative and does not require an LLM.
 */
export async function applyAutomaticMulticam(args: {
    getTranscript: GetTranscriptFn;
    projectId: string;
    style: MulticamStyleOption;
    cutaways: MulticamCutawaysOption;
    layoutPackId?: string;
}): Promise<void> {
    const projectId = String(args.projectId ?? "");
    if (!projectId) return;

    const clipAny = CompositionService.getScriptLayerClip?.();
    if (!clipAny || !isScriptClip(clipAny)) return;
    const scriptClip = clipAny as ScriptLayerClip;

    const inSec = Number((scriptClip as any).range?.[0]?.seconds ?? 0);
    const outSec = Number((scriptClip as any).range?.[1]?.seconds ?? (scriptClip as any).duration?.seconds ?? inSec);
    if (!Number.isFinite(outSec) || outSec <= inSec) return;

    const words = await buildEditedWordsForMulticam({ getTranscript: args.getTranscript, projectId, scriptClip });
    if (!words.length) return;

    const turns = buildSpeakerTurns(words, inSec, outSec);
    if (!turns.length) return;

    // Ensure we have a persisted camera setup mapping. If missing, infer a default mapping:
    // - assign each single-face camera to its matching speaker override (by input),
    // - mark multi-face cameras as "shared".
    try {
        const existingSetup: any = (scriptClip as any).multicamCameraSetup;
        if (!existingSetup || existingSetup.version !== 1) {
            const overrides = Array.isArray((scriptClip as any).speakerOverrides) ? (scriptClip as any).speakerOverrides : [];
            const videoEntries: any[] = typeof (scriptClip as any).getVideoEntries === "function" ? (scriptClip as any).getVideoEntries() : [];
            const audioEntries: any[] = typeof (scriptClip as any).getAudioEntries === "function" ? (scriptClip as any).getAudioEntries() : [];
            const cfgs: any[] = typeof (scriptClip as any).getInputConfigs === "function" ? (scriptClip as any).getInputConfigs() : [];

            const speakerIdByAudioIndex = new Map<number, string>();
            for (const o of overrides) {
                const speakerId = typeof (o as any)?.speakerId === "string" ? String((o as any).speakerId) : "";
                const srcIdx = Number.isFinite((o as any)?.sourceIndex) ? Number((o as any).sourceIndex) : NaN;
                if (!speakerId || !Number.isFinite(srcIdx) || srcIdx < 0) continue;
                if (!speakerIdByAudioIndex.has(srcIdx)) speakerIdByAudioIndex.set(srcIdx, speakerId);
            }

            const assignments: any[] = [];
            for (let vIdx = 0; vIdx < videoEntries.length; vIdx++) {
                const vEntry = videoEntries[vIdx];
                const input = vEntry?.input;
                const cfg = input ? cfgs.find((c: any) => c?.input === input) : undefined;
                const trackId = String(cfg?.assetId ?? "");
                const faces = trackId ? FocusStore.getMedianNumFaces(trackId) : null;

                const audioIdx = input ? audioEntries.findIndex((e: any) => e?.input === input) : -1;
                const speakerId = audioIdx >= 0 ? speakerIdByAudioIndex.get(audioIdx) : undefined;

                if (speakerId && !(faces != null && faces > 1)) {
                    assignments.push({ sourceIndex: vIdx, kind: "speaker", speakerId });
                } else if (faces != null && faces > 1) {
                    assignments.push({ sourceIndex: vIdx, kind: "shared" });
                } else {
                    assignments.push({ sourceIndex: vIdx, kind: "unassigned" });
                }
            }

            if (assignments.length > 0) {
                (scriptClip as any).multicamCameraSetup = { version: 1, assignments };
                try { CompositionService.notifyClipUpdated(scriptClip); } catch {}
            }
        }
    } catch {}

    const minDurationSec =
        args.cutaways === "frequent" ? 1.0 :
        2.2;
    const merged = mergeShortTurns(turns, minDurationSec);
    const shots = planShotsFromTurns({ turns: merged, style: args.style, cutaways: args.cutaways });
    if (!shots.length) return;

    // Overwrite scene boundaries: clear existing scene-boundary splits and sceneProperties.
    try {
        const existing = Array.isArray((scriptClip as any).splits) ? [...(scriptClip as any).splits] : [];
        for (const s of existing) {
            if (s?.isSceneBoundary !== true) continue;
            const id = String(s?.id ?? "");
            if (!id) continue;
            try { (scriptClip as any).removeScriptSplit?.(id); } catch {}
        }
    } catch {}

    try { (scriptClip as any).sceneProperties = new Map(); } catch {}
    try { (scriptClip as any)._scenesCache = null; } catch {}

    // Add new scene splits (skip first, clamp to clip bounds).
    const EPS = 0.05;
    for (let i = 1; i < shots.length; i++) {
        const t = Number(shots[i].startSec ?? NaN);
        if (!Number.isFinite(t)) continue;
        if (t <= inSec + EPS || t >= outSec - EPS) continue;
        try {
            (scriptClip as any).addScriptSplit?.(t, { isSceneBoundary: true });
        } catch {}
    }

    // Notify engine/store once for split changes.
    try {
        const comp: any = CompositionService.composition;
        if (comp) {
            comp.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                clipId: scriptClip.id,
                cuts: (scriptClip as any).scriptCuts,
                gaps: (scriptClip as any).gaps,
                gapInsertions: (scriptClip as any).gapInsertions,
                segments: (scriptClip as any).scriptSegments,
                splits: (scriptClip as any).splits,
                duration: (scriptClip as any).duration?.seconds ?? 0,
                hasVideo: (scriptClip as any).getVideoSources?.()?.length > 0,
            });
            comp.onClipTimingChanged?.();
            comp.trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: comp.scriptLayer?.layerId ?? null,
                    clipId: scriptClip.id,
                    splitsChanged: true,
                },
            });
        }
    } catch {}

    // Hide any layout-managed artifacts from prior layouts (scene keys changed).
    hideAllLayoutManagedClips();

    // Apply per-scene camera layouts.
    const scenes: any[] = typeof (scriptClip as any).getScenes === "function" ? (scriptClip as any).getScenes() : [];
    if (!Array.isArray(scenes) || scenes.length === 0) return;

    const packIdRaw = String(args.layoutPackId ?? "").trim() || DEFAULT_LAYOUT_PACK_ID;
    const pack = LayoutPacksStore.getPack(packIdRaw) ?? BUILTIN_LAYOUT_PACK;
    const cameraTemplate =
        pack.templates.find((t) => t.type === "Camera") ??
        BUILTIN_LAYOUT_PACK.templates.find((t) => normalizeLayoutId(t.id) === "camera") ??
        null;
    const cameraLayoutRef = cameraTemplate
        ? formatLayoutRef(pack.id, normalizeLayoutId(cameraTemplate.id))
        : formatLayoutRef(DEFAULT_LAYOUT_PACK_ID, "camera");

    const aspectPreset = (() => {
        try {
            return (scriptClip as any).getCanvasAspectPreset?.() as any;
        } catch {}
        return "horizontal" as any;
    })();
    const multicamVariantKey = (() => {
        const desired = "two";
        const variants = pack.multicamVariantsByPreset?.[aspectPreset as any] ?? BUILTIN_LAYOUT_PACK.multicamVariantsByPreset?.[aspectPreset as any] ?? [];
        if (Array.isArray(variants) && variants.some((v: any) => String(v?.key ?? "") === desired)) return desired;
        const def =
            String(pack.defaultMulticamVariantKeyByPreset?.[aspectPreset as any] ?? "").trim() ||
            String(BUILTIN_LAYOUT_PACK.defaultMulticamVariantKeyByPreset?.[aspectPreset as any] ?? "").trim() ||
            desired;
        return def || desired;
    })();
    const multicamLayoutRef = formatLayoutRef(pack.id, normalizeLayoutId(`multicam:${multicamVariantKey}`));

    // Build quick shot lookup by scene time (pick shot whose midpoint is within scene).
    const shotForScene = (sceneStart: number, sceneEnd: number): MulticamShot | null => {
        const mid = (sceneStart + sceneEnd) / 2;
        for (const s of shots) {
            if (mid >= s.startSec && mid < s.endSec) return s;
        }
        return shots[shots.length - 1] ?? null;
    };

    const prevSel = ApplicationStore.selectedVisualElement;
    const prevClipId = ApplicationStore.selectedClipId;

    for (const s of scenes) {
        const sceneIndex = Number((s as any)?.index ?? NaN);
        if (!Number.isFinite(sceneIndex) || sceneIndex < 0) continue;
        const startSec = Number((s as any)?.startSec ?? NaN);
        const endSec = Number((s as any)?.endSec ?? startSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec + 1e-3) continue;

        const shot = shotForScene(startSec, endSec);
        if (!shot) continue;

        const mid = (startSec + endSec) / 2;
        if (shot.kind === "two_up") {
            const camA = pickCameraForSpeaker({
                scriptClip,
                speakerId: shot.primary.firestoreSpeakerId,
                speakerKey: shot.primary.speakerKey,
                tSec: mid,
            });
            let camB = pickCameraForSpeaker({
                scriptClip,
                speakerId: shot.secondary.firestoreSpeakerId,
                speakerKey: shot.secondary.speakerKey,
                tSec: mid,
            });
            if (camB === camA) {
                // Try a different camera if available.
                try {
                    const videoEntries: any[] = (scriptClip as any).getVideoEntries?.() ?? [];
                    for (let idx = 0; idx < videoEntries.length; idx++) {
                        if (idx === camA) continue;
                        camB = idx;
                        break;
                    }
                } catch {}
            }

            if (camB !== camA) {
                try {
                    const videoEntries: any[] = (scriptClip as any).getVideoEntries?.() ?? [];
                    for (let idx = 0; idx < videoEntries.length; idx++) {
                        const alpha = idx === camA || idx === camB ? 1 : 0;
                        (scriptClip as any).setSceneVisualProperties?.(sceneIndex, idx, { alpha });
                    }
                } catch {}
                try {
                    LayoutService.applySceneLayout(sceneIndex, multicamLayoutRef);
                } catch {}
                continue;
            }
            // If we couldn't find 2 distinct cameras, fall back to a single-camera shot.
        }

        const turn = shot.primary;
        const camIdx = pickCameraForSpeaker({
            scriptClip,
            speakerId: turn.firestoreSpeakerId,
            speakerKey: turn.speakerKey,
            tSec: mid,
        });
        try {
            ApplicationStore.setSelectedVisualElement({ clipId: scriptClip.id, sourceIndex: camIdx });
        } catch {}
        try {
            LayoutService.applySceneLayout(sceneIndex, cameraLayoutRef);
        } catch {}
    }

    try {
        if (prevSel) ApplicationStore.setSelectedVisualElement(prevSel);
        else ApplicationStore.setSelectedClipId(prevClipId);
    } catch {}

    // Persist full script clip state (splits + sceneProperties) via CLIP_UPDATED.
    try { CompositionService.notifyClipUpdated(scriptClip); } catch {}
}
